import React, { useState, useMemo, useEffect } from "react";

/**
 * GroupSelectionModal
 * Allows users to choose Strategy, Symbol, and Side to filter orders for bulk modification.
 */
export function GroupSelectionModal({ 
  isOpen, 
  onClose, 
  orders, 
  onConfirm,
  assetFilterType = "options", // "options" (CE/PE) or "pledgePlus" (MCX/FUT)
  positions = [] // Used for SL exclusion in pledgePlus mode
}) {
  const [selectedStrategy, setSelectedStrategy] = useState("All");
  const [selectedSymbol, setSelectedSymbol] = useState("All");
  const [selectedSide, setSelectedSide] = useState("All");
  const [error, setError] = useState("");

  // --- Helper: detect if an order is an SL (pending + opposite side to existing position) ---
  const isSLOrder = (order, allPositions) => {
    // Only pending orders (status_no 6) can be SL; traded ones (status_no 4) are filled entries
    const isPending = String(order.status_no) === "6" ||
      ["pending", "open", "trigger pending", "not sent"].includes((order.status || "").toLowerCase());
    if (!isPending) return false;

    const clientId = order.clientId || order.client_id;
    const clientPositions = (allPositions || []).filter(p => {
      const pId = p.clientId || p.client_id;
      return String(pId).trim() === String(clientId).trim();
    });

    // Find a matching position for this order by symbol + strategy
    const relatedPos = clientPositions.find(p => {
      const cleanPSym = (p.symbol || "").toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "").trim();
      const cleanOSym = (order.symbol || "").toUpperCase().replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "").trim();
      if (cleanPSym !== cleanOSym) return false;

      const pStrat = (p.strategy || "").toString().trim().toUpperCase();
      const oStrat = (order.strategy || "").toString().trim().toUpperCase();
      const pClean = pStrat === "N/A" ? "" : pStrat;
      const oClean = oStrat === "N/A" ? "" : oStrat;
      return pClean === oClean;
    });

    if (!relatedPos) return false; // No position found → not SL, treat as ENTRY

    const netQty = parseFloat(relatedPos.net_qty || relatedPos.qty || 0);
    if (netQty === 0) return false; // Flat position → not SL

    const sideStr = String(order.side || "").trim().toLowerCase();
    const isOrderBuy = sideStr === "1" || sideStr === "buy" || sideStr === "b";
    const isPosLong = netQty > 0;

    // SL = pending order on the opposite side of the position
    return (isPosLong && !isOrderBuy) || (!isPosLong && isOrderBuy);
  };

  // --- Filtering based on Dashboard Type ---
  const filteredOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    return orders.filter(o => {
      const oStatus = (o.status || "").toLowerCase();
      // Pending criteria (matches OrdersView and PledgePlusOrdersView)
      const isPending = oStatus === "pending" || oStatus === "open" || oStatus === "trigger pending" || oStatus === "not sent" || String(o.status_no) === "6" || String(o.status_no) === "4";
      
      // Modification focus is typically on Buy orders, but we can allow both if the user wants.
      if (!isPending) return false;

      const sym = (o.symbol || "").toUpperCase();
      const isOption = sym.endsWith("CE") || sym.endsWith("PE") || sym.includes(" CE ") || sym.includes(" PE ");
      const isMcxOrFut = sym.includes("MCX:") || sym.includes("FUT") || sym.endsWith("FUT");

      if (assetFilterType === "options") {
        return isOption && !isMcxOrFut;
      } else if (assetFilterType === "pledgePlus") {
        if (!isMcxOrFut || isOption) return false;
        // Exclude SL orders — these are opposite-side pending orders against existing positions
        if (isSLOrder(o, positions)) return false;
        return true;
      } else if (assetFilterType === "positionSL") {
        if (!isOption || isMcxOrFut) return false;
        // Only include SL orders for position view bulk modify
        if (!isSLOrder(o, positions)) return false;
        return true;
      } else if (assetFilterType === "pledgePlusPositionSL") {
        if (!isMcxOrFut || isOption) return false;
        // Only include SL orders for pledge+ position view bulk modify
        if (!isSLOrder(o, positions)) return false;
        return true;
      }
      return true;
    });
  }, [orders, assetFilterType, positions]);

  // --- Extract Options for Dropdowns ---
  const options = useMemo(() => {
    const strategies = new Set();
    const symbols = new Set();
    const sides = new Set();

    filteredOrders.forEach(o => {
      strategies.add(o.strategy || o.order_tag || "Manual");
      symbols.add((o.symbol || "").toUpperCase());
      
      const side = String(o.side || "").trim().toUpperCase();
      const sideLabel = (side === "1" || side === "BUY") ? "BUY" : "SELL";
      sides.add(sideLabel);
    });

    return {
      strategies: ["All", ...Array.from(strategies).sort()],
      symbols: ["All", ...Array.from(symbols).sort()],
      sides: ["All", "BUY", "SELL"]
    };
  }, [filteredOrders]);

  // Reset selections when modal opens or orders change
  useEffect(() => {
    if (isOpen) {
      setSelectedStrategy("All");
      setSelectedSymbol("All");
      setSelectedSide("All");
      setError("");
    }
  }, [isOpen]);

  // Clear error when selections change
  useEffect(() => {
    setError("");
  }, [selectedStrategy, selectedSymbol, selectedSide]);

  const handleConfirm = () => {
    const finalSelection = filteredOrders.filter(o => {
      const stratMatch = selectedStrategy === "All" || (o.strategy || o.order_tag || "Manual") === selectedStrategy;
      const symMatch = selectedSymbol === "All" || (o.symbol || "").toUpperCase() === selectedSymbol;
      
      const side = String(o.side || "").trim().toUpperCase();
      const sideLabel = (side === "1" || side === "BUY") ? "BUY" : "SELL";
      const sideMatch = selectedSide === "All" || sideLabel === selectedSide;

      return stratMatch && symMatch && sideMatch;
    });

    if (finalSelection.length === 0) {
      setError("No orders match these criteria.");
      return;
    }

    onConfirm(finalSelection);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#14161f] border border-gray-700 rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="bg-[#1c2230] p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="w-2 h-6 bg-indigo-500 rounded-full"></span>
            Bulk Modify Selector
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          <p className="text-gray-400 text-sm">
            Select the group of orders you want to modify bulk across accounts.
          </p>

          {/* Strategy Select */}
          <div className="space-y-2">
            <label className="text-gray-400 text-xs font-bold uppercase tracking-wider">Strategy</label>
            <select 
              value={selectedStrategy}
              onChange={(e) => setSelectedStrategy(e.target.value)}
              className="w-full bg-[#0f1116] border border-gray-700 rounded-lg p-2.5 text-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
            >
              {options.strategies.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Symbol Select */}
          <div className="space-y-2">
            <label className="text-gray-400 text-xs font-bold uppercase tracking-wider">Symbol</label>
            <select 
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              className="w-full bg-[#0f1116] border border-gray-700 rounded-lg p-2.5 text-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
            >
              {options.symbols.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Side Select */}
          <div className="space-y-2">
            <label className="text-gray-400 text-xs font-bold uppercase tracking-wider">Side</label>
            <select 
              value={selectedSide}
              onChange={(e) => setSelectedSide(e.target.value)}
              className="w-full bg-[#0f1116] border border-gray-700 rounded-lg p-2.5 text-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
            >
              {options.sides.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Matches Preview */}
          <div className="bg-[#0f1116] p-3 rounded-lg border border-gray-800 flex justify-between items-center">
            <span className="text-gray-500 text-sm">Actionable Matches:</span>
            <span className="text-indigo-400 font-bold bg-indigo-400/10 px-2 py-0.5 rounded border border-indigo-400/20">
               {filteredOrders.filter(o => {
                  const stratMatch = selectedStrategy === "All" || (o.strategy || o.order_tag || "Manual") === selectedStrategy;
                  const symMatch = selectedSymbol === "All" || (o.symbol || "").toUpperCase() === selectedSymbol;
                  const sideStr = String(o.side || "").trim().toUpperCase();
                  const sideLabel = (sideStr === "1" || sideStr === "BUY") ? "BUY" : "SELL";
                  const sideMatch = selectedSide === "All" || sideLabel === selectedSide;
                  return stratMatch && symMatch && sideMatch;
               }).length}
            </span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-lg animate-in slide-in-from-top-1 duration-200">
              <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-400 text-xs font-medium">{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-[#0f1116] border-t border-gray-800 flex gap-3">
          <button 
            onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleConfirm}
            className="flex-1 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold shadow-lg shadow-indigo-900/40 transition-all"
          >
            Continue to Modify
          </button>
        </div>
      </div>
    </div>
  );
}

export default GroupSelectionModal;
